import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ToastrService } from 'ngx-toastr';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { AccountService } from 'src/app/shared/services/account.service';
import { AccountType } from 'src/app/shared/enums/accountTypes';
import { AppSpinnerModule } from 'src/app/shared/modules/app-spinner.module';
import { AccountSearchResult } from 'src/app/shared/models/account';
import { FormError } from 'src/app/shared/components/app-form-error/form-error';
import { FormValidators } from 'src/app/shared/validators/form.validators';
import { ListViewParticipant, ParticipantModifiable } from 'src/app/shared/models/participant.model';
import { OrderDetailStore } from 'src/app/order-detail/+state/order-detail.store';
import { ParticipantsService } from 'src/app/shared/services/participants.service';

import { FormName, UserRole } from '../participant-form-modal.models';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

@UntilDestroy()
@Component({
  selector: 'app-attorney-information-form',
  templateUrl: './attorney-information-form.component.html',
  styleUrls: ['./attorney-information-form.component.scss'],
  standalone: false
})
export class AttorneyInformationFormComponent implements OnInit {
  attorneyInformationForm: UntypedFormGroup;

  isEditing$: Observable<boolean>;
  participants$: Observable<ListViewParticipant[]>;
  selectedParticipantRole$: Observable<string | null>;
  attorneyRoleValueChanges$: Observable<any> | undefined;

  searchResults: AccountSearchResult[] = [];
  selectedAccount: AccountSearchResult | null = null;
  searchTerm$ = new Subject<string>();
  currentSearchTerm = '';
  readonly minSearchLength = MIN_SEARCH_LENGTH;

  @Input() userRoles: UserRole[];
  @Input() selectedParticipant: ListViewParticipant;
  @Input() orderId: number;

  constructor(
    public store: OrderDetailStore,
    private readonly accountService: AccountService,
    private readonly modalService: NgbModal,
    private readonly spinner: AppSpinnerModule,
    private readonly toastr: ToastrService,
    private readonly participantsService: ParticipantsService
  ) {}

  ngOnInit(): void {
    this.participants$ = this.store.participants$;
    this.isEditing$ = this.store.isEditing$;

    combineLatest([this.participants$, this.isEditing$]).pipe(
      filter(([participants]) => participants !== undefined),
      tap(([_participants, isEditing]) => {
        this.initForm();
        this.watchAndToggleForm();
        this.watchAndUpdateSelectedParticipantRole();
        this.wireUpSearch();
        if (isEditing) {
          this.patchForm();
          this.disableAccountFields();
        }
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  initForm(): void {
    this.attorneyInformationForm = new UntypedFormGroup(
      {
        participantRole: new UntypedFormControl(AccountType.Attorney, {
          validators: [Validators.required],
          updateOn: 'change',
        }),
        existingAttorney: new UntypedFormControl(null),
        emailAddress: new UntypedFormControl('', [
          Validators.maxLength(128),
          Validators.email,
          Validators.required,
        ]),
        confirmEmailAddress: new UntypedFormControl('', [
          Validators.maxLength(128),
          Validators.email,
          Validators.required,
        ]),
        firstName: new UntypedFormControl('', [
          Validators.maxLength(200),
          Validators.required,
        ]),
        lastName: new UntypedFormControl('', [
          Validators.maxLength(200),
          Validators.required,
        ]),
      },
      {
        updateOn: 'blur',
        validators: [FormValidators.confirmEmailAddressValidator('emailAddress', 'confirmEmailAddress')]
      }
    );
  }

  patchForm(): void {
    this.attorneyInformationForm.get('participantRole')?.setValue(
      this.selectedParticipant?.participantModifiable?.role,
      { onlySelf: true }
    );
    this.attorneyInformationForm.get('emailAddress')?.setValue(
      this.selectedParticipant?.participantModifiable?.email,
      { onlySelf: true }
    );
    this.attorneyInformationForm.get('confirmEmailAddress')?.setValue(
      this.selectedParticipant?.participantModifiable?.email,
      { onlySelf: true }
    );
    this.attorneyInformationForm.get('firstName')?.setValue(
      this.selectedParticipant?.participantModifiable?.firstName,
      { onlySelf: true }
    );
    this.attorneyInformationForm.get('lastName')?.setValue(
      this.selectedParticipant?.participantModifiable?.lastName,
      { onlySelf: true }
    );
  }

  watchAndToggleForm(): void {
    this.attorneyRoleValueChanges$ = this.attorneyInformationForm.get('participantRole')?.valueChanges.pipe(
      distinctUntilChanged(),
      tap(value => {
        if (value === AccountType.Attorney) {
          return;
        }
        if (value === AccountType.SigningAgent) {
          this.store.setSelectedParticipantRole(value);
          this.store.setCurrentForm(FormName.SigningAgentInformation);
          return;
        }
        if (value === AccountType.Witness) {
          this.store.setSelectedParticipantRole(value);
          this.store.setCurrentForm(FormName.WitnessInformation);
          return;
        }
        if (value === AccountType.NonBorrowingTitleHolder) {
          this.store.setSelectedParticipantRole(value);
          this.store.setCurrentForm(FormName.NonBorrowingTitleHolderInformation);
          return;
        }
        this.store.setSelectedParticipantRole(value);
        this.store.setCurrentForm(FormName.ParticipantInformation);
      }),
      untilDestroyed(this)
    );
    this.attorneyRoleValueChanges$?.subscribe();
  }

  watchAndUpdateSelectedParticipantRole(): void {
    this.selectedParticipantRole$ = this.store.selectedParticipantRole$.pipe(
      tap(selectedParticipantRole => {
        this.attorneyInformationForm.get('participantRole')?.setValue(selectedParticipantRole);
      }),
      untilDestroyed(this)
    );
    this.selectedParticipantRole$.subscribe();
  }

  wireUpSearch(): void {
    this.searchTerm$.pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap(term => {
        if (!term || term.trim().length < MIN_SEARCH_LENGTH) {
          return of([] as AccountSearchResult[]);
        }
        return this.accountService.searchAccounts(this.orderId, term.trim()).pipe(
          catchError(() => of([] as AccountSearchResult[]))
        );
      }),
      tap(results => {
        this.searchResults = results;
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target?.value ?? '';
    this.currentSearchTerm = value.trim();
    this.searchTerm$.next(value);
  }

  onAttorneyDropdownOpened(inputElement: HTMLInputElement): void {
    inputElement.value = '';
    this.currentSearchTerm = '';
    this.searchResults = [];
  }

  onAttorneyChanged(value: AccountSearchResult | 'removeSelection'): void {
    if (value === 'removeSelection') {
      this.clearSelectedAccount();
      return;
    }
    this.selectAccount(value);
  }

  private selectAccount(account: AccountSearchResult): void {
    this.selectedAccount = account;
    this.attorneyInformationForm.get('firstName')?.setValue(account.firstName);
    this.attorneyInformationForm.get('lastName')?.setValue(account.lastName);
    this.attorneyInformationForm.get('emailAddress')?.setValue(account.emailAddress);
    this.attorneyInformationForm.get('confirmEmailAddress')?.setValue(account.emailAddress);
    this.disableAccountFields();
  }

  private clearSelectedAccount(): void {
    this.selectedAccount = null;
    this.attorneyInformationForm.get('existingAttorney')?.setValue(null);
    this.attorneyInformationForm.get('firstName')?.setValue('');
    this.attorneyInformationForm.get('lastName')?.setValue('');
    this.attorneyInformationForm.get('emailAddress')?.setValue('');
    this.attorneyInformationForm.get('confirmEmailAddress')?.setValue('');
    this.enableAccountFields();
  }

  private disableAccountFields(): void {
    this.attorneyInformationForm.get('firstName')?.disable();
    this.attorneyInformationForm.get('lastName')?.disable();
    this.attorneyInformationForm.get('emailAddress')?.disable();
    this.attorneyInformationForm.get('confirmEmailAddress')?.disable();
  }

  private enableAccountFields(): void {
    this.attorneyInformationForm.get('firstName')?.enable();
    this.attorneyInformationForm.get('lastName')?.enable();
    this.attorneyInformationForm.get('emailAddress')?.enable();
    this.attorneyInformationForm.get('confirmEmailAddress')?.enable();
  }

  resetModal(reason: 'cancel' | 'saved'): void {
    this.modalService.dismissAll(reason);
    this.store.setSelectedParticipantRole(null);
    this.store.setIsEditing(false);
    this.spinner.hide();
  }

  addParticipant(): void {
    if (this.attorneyInformationForm.valid) {
      this.spinner.show();
      this.removeWhiteSpaceFromAllFields();
      const participant = this.createParticipant();
      this.participantsService.addParticipant(this.orderId, participant).subscribe(
        () => {
          this.onParticipantSaveSuccess();
          this.spinner.hide();
        },
        (error: any) => {
          this.onParticipantSaveError(error);
          this.spinner.hide();
        }
      );
    } else {
      FormError.ValidateAllFormFields(this.attorneyInformationForm);
    }
  }

  updateParticipant(): void {
    if (this.attorneyInformationForm.valid) {
      this.spinner.show();
      this.removeWhiteSpaceFromAllFields();
      const participant = this.createParticipant();
      this.participantsService.updateParticipant(this.selectedParticipant.id, participant).subscribe(
        () => {
          this.onParticipantSaveSuccess();
          this.spinner.hide();
        },
        (error: any) => {
          this.onParticipantSaveError(error);
          this.spinner.hide();
        }
      );
    } else {
      FormError.ValidateAllFormFields(this.attorneyInformationForm);
    }
  }

  private removeWhiteSpaceFromAllFields(): void {
    Object.keys(this.attorneyInformationForm.controls).forEach(key => {
      const currentValue = this.attorneyInformationForm.get(key)?.value;
      if (typeof currentValue === 'string') {
        this.attorneyInformationForm.get(key)?.setValue(currentValue.trim());
      }
    });
  }

  private createParticipant(): ParticipantModifiable {
    const formValue = this.attorneyInformationForm.getRawValue();
    return {
      email: formValue.emailAddress,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: '',
      participantAddress: null,
      role: formValue.participantRole,
      sequenceNumber: 1
    };
  }

  private onParticipantSaveSuccess(): void {
    this.toastr.success('Participant saved successfully.');
    this.resetModal('saved');
  }

  private onParticipantSaveError(error: any): void {
    this.toastr.error(error?.error?.error ?? error?.message ?? 'Unable to save participant.');
  }
}
